'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:tillamook-com":true});}catch(e){}}();

import React, { useCallback, useEffect, useMemo, useState } from 'react';

import cx from 'classnames';
import copy from 'clipboard-copy';
import PropTypes from 'prop-types';

import SvgIcon from 'components/ui/SvgIcon';
import icons from 'components/ui/SvgIcon/icons';

import { event as gtmEvent } from 'utils/gtm';

import styles from './ShareButtons.module.scss';

const withUtm = (url, source) => {
    try {
        const u = new URL(url);
        u.searchParams.set('utm_source', source);
        u.searchParams.set('utm_medium', 'share');
        u.searchParams.set('utm_campaign', 'organic_share');
        return u.toString();
    } catch {
        return url;
    }
};

const buildIntentUrl = ({ platform, url, title, text, image }) => {
    const u = encodeURIComponent(withUtm(url, platform));
    const t = encodeURIComponent(text || title || '');
    switch (platform) {
        case 'facebook':
            return `https://www.facebook.com/sharer/sharer.php?u=${u}`;
        case 'pinterest': {
            const media = image ? `&media=${encodeURIComponent(image)}` : '';
            return `https://www.pinterest.com/pin/create/button/?url=${u}${media}&description=${t}`;
        }
        case 'email': {
            const subject = encodeURIComponent(title || '');
            return `mailto:?subject=${subject}&body=${t}%20${u}`;
        }
        default:
            return url;
    }
};

const supportedIntentPlatforms = ['facebook', 'pinterest', 'email'];

const ShareButtons = ({
    shareUrl = '',
    shareTitle = '',
    shareText = '',
    shareImage = '',
    theme = '',
}) => {
    const [url, setUrl] = useState(shareUrl);
    const [toast, setToast] = useState('');
    const [canNativeShare, setCanNativeShare] = useState(false);

    useEffect(() => {
        if (!shareUrl && typeof window !== 'undefined') {
            setUrl(window.location.href);
        } else {
            setUrl(shareUrl);
        }
        if (typeof navigator !== 'undefined' && navigator.share) {
            setCanNativeShare(true);
        }
    }, [shareUrl]);

    const meta = useMemo(() => {
        return {
            url,
            title: shareTitle || '',
            text: shareText || '',
        };
    }, [url, shareTitle, shareText]);

    const copyToClipboard = useCallback(async () => {
        try {
            await copy(url);
            setToast('Link copied to clipboard');
            gtmEvent('share_copy', { platform: 'copy', url });
            window.setTimeout(() => setToast(''), 2000);
        } catch {
            setToast('Copy failed');
            window.setTimeout(() => setToast(''), 2000);
        }
    }, [url]);

    const onNativeShare = useCallback(async () => {
        if (!canNativeShare) return;
        try {
            await navigator.share({
                title: meta.title,
                text: meta.text,
                url: withUtm(meta.url, 'native'),
            });
            gtmEvent('share_native', { platform: 'native', url: meta.url });
        } catch {}
    }, [canNativeShare, meta]);

    const intents = useMemo(() => {
        return supportedIntentPlatforms.map(platform => ({
            platform,
            href: buildIntentUrl({
                platform,
                url: meta.url,
                title: meta.title,
                text: meta.text,
                image: shareImage,
            }),
            label: `Share on ${
                platform.charAt(0).toUpperCase() + platform.slice(1)
            }`,
            icon: platform,
        }));
    }, [meta, shareImage]);

    return (
        <div
            className={cx(styles.root, {
                [styles.sansEmail]: theme === 'Sans Email',
            })}
            role="region"
            aria-label="Share this page"
        >
            {canNativeShare && icons['share'] && (
                <button
                    type="button"
                    className={cx(styles.icon, styles.button, styles.native)}
                    aria-label="Share"
                    onClick={onNativeShare}
                >
                    <SvgIcon type="share" />
                </button>
            )}

            <button
                type="button"
                className={cx(styles.icon, styles.button, styles.copy)}
                aria-label="Copy link"
                onClick={copyToClipboard}
            >
                {icons['link'] ? <SvgIcon type="link" /> : 'Copy'}
            </button>

            {intents.map(({ platform, href, label, icon }) => (
                <a
                    key={platform}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cx(styles.icon, styles.intent, styles[platform])}
                    aria-label={label}
                    onClick={() => gtmEvent('share_intent', { platform, url })}
                >
                    <SvgIcon type={icon} />
                </a>
            ))}

            <span
                className={cx(styles.toast)}
                aria-live="polite"
                aria-atomic="true"
            >
                {toast}
            </span>
        </div>
    );
};

ShareButtons.propTypes = {
    shareUrl: PropTypes.string,
    shareTitle: PropTypes.string,
    shareText: PropTypes.string,
    shareImage: PropTypes.string,
    theme: PropTypes.oneOf(['', 'Sans Email']),
};

export default ShareButtons;
